import React, { FC, useCallback, useState, useRef, useEffect } from 'react';
import { GoogleMap, Marker, MarkerClusterer } from '@react-google-maps/api';
import { ClustererOptions, Clusterer } from '@react-google-maps/marker-clusterer';

//Import default map style
import styles from './map-styles';
import { MapMarker } from '../models';
import { isChecked } from '../utils/isChecked';
import { useFrame } from '~/shared/utils';
import { prettifyUrl } from '~/templates/pages/utils/prettifyUrl';
import { useRouter } from 'next/router';
import useGoogleMapsApi from '../hooks/useGoogleMapsApi';

//Documentation
//https://react-google-maps-api-docs.netlify.app/
const containerStyle = {
    width: '100%',
    height: '100%',
};

interface IGoogle {
    // eslint-disable-next-line no-undef
    maps: typeof google.maps;
}

interface IGMapWindow extends Window {
    google: IGoogle;
}

//eslint-disable-next-line
declare let window: IGMapWindow;

type MapHelper = {
    markers?: MapMarker[];
    center?: google.maps.LatLngLiteral;
    onScriptLoadCallback?: () => void;
    onMapLoadCallback?: (map: google.maps.Map) => void;
    markerIcon?: string | google.maps.Icon | google.maps.Symbol;
    selectedMarkerIcon?: string | google.maps.Icon | google.maps.Symbol;
    clusterOptions?: ClustererOptions;
    onPinClick?: (marker: MapMarker) => void;
    onDragEnd?: () => void;
    selectedPin?: any;
};

const MapHelper: FC<MapHelper> = (props) => {
    const {
        markers = [],
        markerIcon,
        selectedMarkerIcon,
        clusterOptions,
        onPinClick,
        onScriptLoadCallback,
        onMapLoadCallback,
        selectedPin,
        children,
        center,
    } = props;

    const { isLoaded } = useGoogleMapsApi();

    const onLoad = useCallback(
        (_map) => {
            if (onMapLoadCallback) {
                onMapLoadCallback(_map);
            }
            setMap(_map);
        },
        [onMapLoadCallback],
    );

    const [, setMap] = useState<google.maps.Map | null>(null);
    const clustererRef = useRef<Clusterer | null>(null);
    const { data: frame } = useFrame();
    const router = useRouter();

    useEffect(() => {
        if (markers && clustererRef.current) {
            clustererRef.current.repaint();
        }
    }, [markers, clustererRef.current]);

    useEffect(() => {
        if (isLoaded && onScriptLoadCallback) {
            onScriptLoadCallback();
        }
    }, [isLoaded, onScriptLoadCallback]);

    const storesUrl = frame?.settings?.pageReferences.storesPage?.url;

    return isLoaded ? (
        <GoogleMap
            options={{
                styles,
                disableDefaultUI: true,
                zoomControl: true,
                isFractionalZoomEnabled: true,
            }}
            mapContainerStyle={containerStyle}
            center={center}
            onLoad={onLoad}
        >
            {children}
            {/* {markers && ( */}
            <MarkerClusterer
                options={clusterOptions}
                ignoreHidden={true}
                onLoad={(clusterer): void => {
                    clustererRef.current = clusterer;
                }}
            >
                {(cluster): JSX.Element[] =>
                    markers.map((marker: MapMarker) => {
                        const urlStore = marker?.name
                            ? `${storesUrl}/${prettifyUrl(marker?.name)}?storeid=${marker.id}`
                            : '';
                        return (
                            <Marker
                                title={marker.name}
                                onClick={(): void => {
                                    if (onPinClick) {
                                        onPinClick(marker);
                                    }
                                    router.push(urlStore);
                                }}
                                visible={true}
                                icon={
                                    isChecked({ selected: selectedPin, option: marker })
                                        ? selectedMarkerIcon
                                        : markerIcon
                                }
                                key={marker?.id ?? marker._id}
                                position={marker}
                                clusterer={cluster}
                            />
                        );
                    })
                }
            </MarkerClusterer>
            {/* )} */}
        </GoogleMap>
    ) : null;
};

export default MapHelper;
